import { useRef, forwardRef, useMemo, useEffect, Fragment } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useForm, useFormContext, FormProvider, Controller } from 'react-hook-form';
// Models
import { IDocument } from 'app/store/DMSDocuments/DMSDocuments.models';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getBatch } from 'app/store/DMSBatches/DMSBatches.async';
import { createDocument, patchDocument } from 'app/store/DMSDocuments/DMSDocuments.async';
import { useCreateCommentMutation, useDeleteCommentMutation, useGetDocumentCommentsQuery, useGetDocumentQuery, useUpdateCommentMutation } from 'app/store/DMSDocuments/DMSDocuments.api';
// Actions
import { DMSBatchesActions } from 'app/store/DMSBatches/DMSBatches.slice';

import { Box } from '@mui/material';

import { Toolbar, Editor } from 'app/components/QuillEditor';
import Dialog from 'app/components/Dialog';

import DialogToolbar from './DialogToolbar';
import DocumentComments from './DocumentComments';

import { applyCommentFormatting, clearQuillInstance } from './documentQuillUtils';
import { useTranslation } from 'react-i18next';

import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
import Quill from 'quill';
import useHTMLLinkClickHandler from 'app/pages/Staff/CaseDetailsPage/AIChatPage/MessageListItems/useHTMLLinkClickHandler';
import { selectPreviewPageOpen } from 'app/store/AppUI/AppUI.selectors';
import { AppUIActions } from 'app/store/AppUI/AppUI.slice';

interface IFormData {
  collectionId: string;
  name: string;
  content: string;
  comments: any[]; // Comments will be stored here
}

type Props = {
  open: boolean;
  onClose: () => void;
  // `content` is generate when document is created from template
  content?: string;
  // `document` is the original document object when editing opened from document list
  document?: IDocument;
}

export default function DocumentFormDialog({ open, onClose, content, document }: Props) {
  const dispatch = useAppDispatch();

  const history = useHistory();

  const previewPageOpen = useAppSelector(selectPreviewPageOpen);

  // This will fetch document from sidebar
  // Since you might have selected one document and edit another one
  const { isLoading:documentLoading, data:documentData } = useGetDocumentQuery(document?.id, {
    skip: !document?.id,
    refetchOnMountOrArgChange: true, // Fetch new data on each mount or arg change
  });
  const { isLoading:commentLoading, data:commentsData } = useGetDocumentCommentsQuery(document?.id, {
    skip: !document?.id,
    refetchOnMountOrArgChange: true, // Fetch new data on each mount or arg change
  });
  const [ createComment ] = useCreateCommentMutation();
  const [ updateComment ] = useUpdateCommentMutation();
  const [ deleteComment ] = useDeleteCommentMutation();

  const isLoading = documentLoading || commentLoading;

  const { caseId } = useParams<{ caseId:string }>();

  const initialContent = useRef<string>('');

  const methods = useForm<IFormData>({
    defaultValues: {
      name: document?.name || '',
      collectionId: document?.collectionId.toString() || '',
      content: document?.content || content || '',
      comments: [], // Comments will be stored here
    }
  });

  const quillRef = useRef<{getQuill: () => any | null}>(null);
  const originalComments = useRef<any[]>([]); // To store original comments when editing existing document

  // Merge `document` from props and `documentData` to one variable
  const selectedDocument = useMemo(() => {
    return documentData || document;
  }, [document, documentData]);

  useEffect(() => {
    return () => {
      dispatch(AppUIActions.setPreviewPageOpen(false));
      dispatch(AppUIActions.setPreviewPage(null));
      dispatch(AppUIActions.setReferencePage(null));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( !document?.id ) return;
    if ( !documentData || !commentsData ) return;

    methods.reset({
      name: documentData.name,
      collectionId: documentData.collectionId.toString(),
      content: documentData.content,
      comments: commentsData,
    });
    originalComments.current = commentsData;
  }, [document, documentData, commentsData, methods]);

  const onSubmit = methods.handleSubmit((data:IFormData) => {
    const { comments, collectionId, content:_unused, ...restData } = data;

    if ( comments.length > 0 ) {
      for ( const comment of comments ) {
        applyCommentFormatting(quillRef, comment.anchor);
      }
    }

    const quill = clearQuillInstance(quillRef);

    if ( !quill ) return;

    const newData:any = {
      ...restData,
      collectionId: Number(collectionId),
    };

    newData.content = quill.root.innerHTML;

    if ( !selectedDocument ){
      newData.insuranceCaseId = Number(caseId);
      asyncCreateDocument(newData, comments);
    } else {
      const changedFields = getChangedFields(selectedDocument, newData);
      asyncUpdateDocument(selectedDocument.id, changedFields, comments);
    }
  });

  const asyncUpdateComments = async (documentId:number, comments:any[]) => {
    const createdComments = comments.filter(c => {
      return !originalComments.current.some(oc => oc.id === c.id);
    });
    const deletedComments = originalComments.current.filter(oc => !comments.some(c => c.id === oc.id));
    const updatedComments = comments.filter(c => {
      const original = originalComments.current.find(oc => oc.id === c.id);
      return original && JSON.stringify(original) !== JSON.stringify(c);
    });
    try {
      if ( createdComments.length > 0 ) {
        for ( const comment of createdComments ) {
          await createComment({
            documentId,
            data: { content: comment.content, anchor: comment.anchor }
          }).unwrap();
        }
      }
      if ( updatedComments.length > 0 ) {
        for ( const comment of updatedComments ) {
          await updateComment({
            documentId,
            commentId: comment.id,
            data: { content: comment.content, anchor: comment.anchor }
          }).unwrap();
        }
      }
      if ( deletedComments.length > 0 ) {
        for ( const comment of deletedComments ) {
          await deleteComment({ documentId, commentId: comment.id }).unwrap();
        }
      }
    } catch(error){
      console.log(error);
    }
  }

  const asyncCreateDocument = async (data:any, comments:any[]) => {
    try {
      const documentResponse = await dispatch(createDocument(data)).unwrap();
      dispatch(MixpanelActions.handleCaseDocumentCreatedClick({ documentId: documentResponse.id }));
      const batchResponse = await dispatch(getBatch(documentResponse.batchId)).unwrap();
      await asyncUpdateComments(documentResponse.id, comments);
      dispatch(DMSBatchesActions.addBatch(batchResponse));
      history.push(`/admin/cases/${caseId}/documents/${documentResponse.id}`);
      handleClose(true);
    } catch(error){
      console.log(error);
    }
  }

  const asyncUpdateDocument = async (documentId:number, data:any, comments:any[]) => {
    try {
      if (Object.keys(data).length > 0) {
        await dispatch(patchDocument({ documentId, data })).unwrap();
      }
      await asyncUpdateComments(documentId, comments);
      handleClose(true);
    } catch(error){
      console.log(error);
    }
  }

  const handleClose = (skipDirtyCheck:boolean = false) => {
    if ( skipDirtyCheck ) {
      clearQuillInstance(quillRef);
      onClose();
      return;
    }

    const initialData:any = {
      name: selectedDocument?.name || '',
      collectionId: selectedDocument?.collectionId.toString() || '',
      comments: commentsData || [],
      content: initialContent.current || '',
    };
    const { content:_unused, ...restValues } = methods.getValues();
    const currentData = { ...restValues, content: quillRef.current?.getQuill()?.root.innerHTML || '' };
    if (isDirty(initialData, currentData)) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }

    clearQuillInstance(quillRef);
    onClose();
  }

  const id = 'document-editor';

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      fullScreen
      sx={{ zIndex: 1298 }}
      PaperProps={{ sx: { border: 0 } }}
      ContentProps={{ sx: { border: 0, p: 0 } }}
    >
      <FormProvider {...methods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
          component={'form'}
          onSubmit={onSubmit}
          noValidate
        >
          <DialogToolbar onClose={() => handleClose()} />
          {isLoading ? null : (
            <Fragment>
              <DocumentToolbar id={id} />
              <Box sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: previewPageOpen ? 'flex-end' : 'center',
                gap: 2,
                bgcolor: '#f7f7f7',
                py: 4,
                px: 6,
                overflowY: 'auto'
              }}>
                <DocumentEditor ref={quillRef} id={id} onSetInitialContent={(content) => {
                  initialContent.current = content;
                }} />
                <DocumentComments quillRef={quillRef} />
              </Box>
            </Fragment>
          )}
        </Box>
      </FormProvider>
    </Dialog>
  )
}

type DocumentToolbarProps = {
  id: string;
}
const DocumentToolbar = ({ id }: DocumentToolbarProps) => {
  const toolbarProps = {
    id,
    isTabStopVisible: true,
    isPageBreakVisible: true,
    isTableVisible: true
  };
  return (
    <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)', px: 6, py: 2 }}>
      <Toolbar {...toolbarProps} />
    </Box>
  )
};

type DocumentEditorProps = {
  id: string;
  onSetInitialContent: (content: string) => void;
}
const DocumentEditor = forwardRef(({ id, onSetInitialContent }: DocumentEditorProps, ref) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const { containerRef, setLinkPreview, clearLinkPreview } = useHTMLLinkClickHandler({
    disableLinkPreview: true
  });

  useEffect(() => {
    const quill = (ref as React.RefObject<{getQuill: () => Quill | null}>).current?.getQuill();
    if (!quill) return;
    const tooltip = (quill.theme as any).tooltip;
    if (!tooltip) return;
    const { clickedLink, previewButton, editButton } = tooltip;
    // Store handler references so we can remove them
    const onPreviewClick = () => {
      if ( clickedLink ) setLinkPreview(clickedLink);
      setTimeout(() => {
        if (tooltip && typeof tooltip.hide === 'function') {
          tooltip.hide();
          quill.root.focus();
        }
      }, 10);
    };
    const onEditClick = () => {
      clearLinkPreview();
    };
    if ( previewButton ) previewButton.addEventListener('click', onPreviewClick);
    editButton.addEventListener('click', onEditClick);
    // Cleanup: remove listeners
    return () => {
      if ( previewButton ) previewButton.removeEventListener('click', onPreviewClick);
      editButton.removeEventListener('click', onEditClick);
    };
  }, [ref, setLinkPreview, clearLinkPreview, containerRef]);

  return (
    <Box sx={{ width: '100%', maxWidth: 816 }} ref={containerRef}>
      <Controller
        name="content"
        control={control}
        render={({ field }) => (
          <Editor
            {...field}
            id={id}
            ref={ref}
            label={t('labels.content')}
            isTableVisible={true}
            formControlSx={{
              overflow: 'initial !important',
              height: 'auto !important'
            }}
            paperSx={{
              '& .ql-editor': {
                minHeight: 1123,
                py: '19.2px',
                pl: '60.48px',
                pr: '36.48px',
                '&.ql-blank::before': {
                  left: '60.48px',
                  right: '36.48px',
                  top: '19.2px',
                  bottom: '19.2px'
                }
              }
            }}
            onSetInitialContent={onSetInitialContent}
            tooltipOptions={{
              showPreviewButton: true
            }}
          />
        )}
      />
    </Box>
  )
});

function isDirty(initial: any, current: any): boolean {
  const currentComments = (current.comments || []).map((c: any) => {
    const { status, ...rest } = c;
    return rest;
  });
  const currentContent = cleanupContent(current.content, initial.comments);
  return (
    initial.name !== current.name ||
    initial.collectionId !== current.collectionId ||
    initial.content !== currentContent ||
    !areCommentsEqual(initial.comments, currentComments)
  );
}

function areCommentsEqual(a: any[], b: any[]): boolean {
  if (a?.length !== b?.length) return false;
  const sortById = (arr: any[]) => [...(arr || [])].sort((x, y) => (x.id ?? '').toString().localeCompare((y.id ?? '').toString()));
  const arrA = sortById(a);
  const arrB = sortById(b);

  for (let i = 0; i < arrA.length; i++) {
    if (!deepEqual(arrA[i], arrB[i])) return false;
  }
  return true;
}

// Deep equality check for objects and arrays
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a === null || b === null) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}

const cleanupContent = (content:string, comments:any[]) => {
  let modifiedContent = content;
  if ( comments && comments.length ){
    for ( const comment of comments ) {
      const spanRegex = new RegExp(`<span[^>]*data-comment-id=["']${comment.id}["'][^>]*>(.*?)<\/span>`, 'g');
      modifiedContent = modifiedContent.replace(spanRegex, '$1');
    }
  };
  // Remove class attributes from all anchor tags
  modifiedContent = modifiedContent.replace(/<a([^>]*?)\sclass\s*=\s*(['"]).*?\2([^>]*)>/gi, '<a$1$3>');
  // Remove draggable attribute from all <img> tags (handles quoted and unquoted values)
  modifiedContent = modifiedContent.replace(/(<img\b[^>]*?)\sdraggable\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '$1');
  // Remove "ql-cell-focused" from any class attributes (handles single/double/unquoted)
  modifiedContent = modifiedContent.replace(/\sclass\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi, (_m, g1, g2, g3) => {
    const cls = g1 ?? g2 ?? g3 ?? '';
    const classes = cls.split(/\s+/).filter((c:string) => c && c !== 'ql-cell-focused');
    return classes.length ? ` class="${classes.join(' ')}"` : '';
  });
  return modifiedContent; 
}

function getChangedFields(initial:any, current:any) {
  const changed:any = {};

  if (initial.name !== current.name) changed.name = current.name;
  if (initial.collectionId !== current.collectionId) changed.collectionId = Number(current.collectionId);

  const currentContent = cleanupContent(current.content, initial.comments);

  if (initial.content !== currentContent) changed.content = current.content;

  return changed;
}
